import { useEffect, useState, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import cn from 'classnames'
import styles from './Action.module.sass'
import Icon from '../../../../../../components/Icon'
import TPSLPrice from './TPSLPrice'
import { useTranslation } from 'react-i18next'
import { postOrderAPI } from 'apis/orders'
import { useDispatch, useSelector } from 'react-redux';
import { calculateTradingFee } from 'utils/tradingFees';
import Loader from 'components/Loader'
import { toast } from 'react-toastify'
import TriggerOrder from './TriggerOrder'
import { refetchOrders } from 'store/crypto/orders/actions'
import FormattedInput from './debouncedInput'
import TwoFactorAuth from 'components/TwoFactorAuth/VerifyTwoFactorAuthModal'
import { EnableTwoFactorAuthModal } from 'components/TwoFactorAuth/EnableTwoFactorAuthModal'
import { fetchProfile } from 'store/actions'
import { verify2FACodeStart } from 'store/general/auth/twoFactorAuth/actions'
import MaxField from './AmountField'

const COOptions = ['Conditional', 'OCO']
const LoMoOptions = ['Limit order', 'Market order']
const TpLimitOptions = ['TP Limit', 'TP Market']
const SlLimitOptions = ['SL Limit', 'SL Market']
import { toggleCurrentModal } from "store/actions";

const Action = ({
  buttonText,
  conditionalOrOCO,
  content,
  classButton,
  priceSymbol,
  amountSymbol,
  stop,
  title,
  takeProfit,
  stopLoss,
  actionType,
  type,
  currentPrice,
  klinesSymbol,
  amountPrecision,
  pricePrecision
}) => {
  const { t } = useTranslation()
  const [conditional, setConditional] = useState(COOptions[0])
  const [isTakeProfit, setIsTakeProfit] = useState(false)
  const [isStopLoss, setIsStopLoss] = useState(false)
  const [loMoOption, setLoMoOptions] = useState(LoMoOptions[0])
  const [tpLimitOption, setTpLimitOption] = useState(TpLimitOptions[0])
  const [slLimitOption, setSlLimitOption] = useState(SlLimitOptions[0])
  const [loading, setLoading] = useState(false)
  const [orderType, setOrderType] = useState('limit')

  const [triggerOrderType, setTriggerOrderType] = useState('Limit') // Default to Limit order
  const navigate = useNavigate()
  const { clientData } = useSelector((state) => state?.Profile)
  const { verifyCode } = useSelector((state) => state?.twoFactorAuthReducer)
  const dispatch = useDispatch()

  const [feesAmount, setFeesAmount] = useState(0)
  const [price, setPrice] = useState('')
  const [amount, setAmount] = useState('')
  const [showTwoFactorAuthModal, setShowTwoFactorAuthModal] = useState(false)
  const [showEnableTwoFactorAuthModal, setShowEnableTwoFactorAuthModal] = useState(false)
  const [totalPrice, setTotalPrice] = useState(0)
  const [triggerPrice, setTriggerPrice] = useState(price)
  const [slTriggerPrice, setSlTriggerPrice] = useState('')
  const [slLimitTriggerPrice, setSlLimitTriggerPrice] = useState('')
  const [isPriceSetOnce, setIsPriceSetOnce] = useState(false)

  const handleChangeLimitPrices = () => {
    if (price && amount) {
      setTotalPrice(price * amount)
      handleCalculateFeesValue(price * amount)
    }
  }

  const formatToPrecision = (value, precision) => {
    if (!value || isNaN(value)) return "";
    return parseFloat(Number(value).toFixed(precision));
  };

  const handleCalculateFeesValue = (value) => {
    const tradingFee = clientData?.tradingFeeId;
    const fee = calculateTradingFee(tradingFee, type, Number(value));
    setFeesAmount(fee);
    return fee;
  };

  useEffect(() => {
    if (type === "limit" || type === "tpsl" || type === "triggerorder" || type === "postonly") {
      handleChangeLimitPrices()
    }
  }, [price, amount])

  useEffect(() => {
    if (type === "market" && amount && currentPrice ) {
      handleCalculateFeesValue(amount * currentPrice)
    }
  }, [amount, currentPrice])

  // Initialize price when component mounts or update when klinesSymbol changes
  useEffect(() => {
    if ((type === "limit" || type === "tpsl" || type === "postonly" || type === "triggerorder") &&
      (currentPrice) ) {

      setPrice(formatToPrecision(currentPrice, pricePrecision))
    }
  }, [klinesSymbol, type, currentPrice, isPriceSetOnce]) // Depend on klinesSymbol, type, and currentPrice

  // console.log('price is ' ,pricingData, price)
  const handleMaxClick = async (totp) => {
    try {
      if (clientData?.settings?.twoFactorAuthEnabled && !totp) {
        dispatch(toggleCurrentModal("verifyTwoFactorAuthModal"+buttonText))
        return
      }
      if(!clientData?.settings?.twoFactorAuthEnabled && !totp) {
        dispatch(toggleCurrentModal("enableTwoFactorAuthModal"+buttonText))
        return
      }
      
      // Verify TOTP code if provided
      if (totp) {
        const verificationType = clientData?.settings?.twoFactorAuthEnabled ? 'login' : 'enable'
        
        try {
          await dispatch(verify2FACodeStart({
            token: totp,
            email: clientData && clientData.email && clientData.email.toLowerCase(),
            type: verificationType,
          }))
          
          // Check if verification was successful
          if (verifyCode?.error) {
            toast.error('Invalid 2FA code')
            return
          }
        } catch (error) {
          toast.error('2FA verification failed')
          return
        }
      }
      
      // Set maximum available amount after 2FA validation
      if (content && typeof content === 'string') {
        let availableBalance = parseFloat(content.replace(/[^\d.-]/g, ''))
        if(isNaN(availableBalance))
          availableBalance = 0  
        if (actionType === 'buy' && (price || currentPrice)) {
            // For buy orders, calculate max amount based on available quote currency and price
            const maxAmount = formatToPrecision(availableBalance / (price || currentPrice), amountPrecision)
            setAmount(maxAmount)
          } else if (actionType === 'sell') {
            // For sell orders, use available base currency
            setAmount(formatToPrecision(availableBalance, amountPrecision)||0)
        }
      }
    } catch (error) {
      console.error('Error setting max amount:', error)
      toast.error('Failed to set maximum amount')
    }
  }

  const handleClickWithData = async (totp) => {
    try {
      if (clientData?._id) {
        setLoading(true)
        let params = {}
        if (type === "limit") {
          params = {
            market: { pairName: klinesSymbol },
            type: actionType,
            orderType: type,
            baseAmount: amount,
            quoteAmount: price,
          }
        } else if (type === "market") {
          params = {
            market: { pairName: klinesSymbol   },
            type: actionType,
            orderType: type,
            baseAmount: amount,
          }
        } else if (type === "postonly") {
          params = {
            market: { pairName: klinesSymbol },
            type: actionType,
            orderType: "post-only",
            baseAmount: amount,
            quoteAmount: price,
          }
        } else if (type === "triggerorder") {
          params = {
            market: { pairName: klinesSymbol },
            type: actionType,
            orderType: "triggerorder",
            baseAmount: amount,
            price: triggerOrderType.toLowerCase() === 'market' ? undefined : price,
            triggerPrice: triggerPrice,
            tpType: triggerOrderType.toLowerCase(),
          }
        } else if (type === "tpsl") {
          if (conditional.toLowerCase() === 'conditional') {
            let loMoOptionData
            if (loMoOption === "Limit order") {
              loMoOptionData = "limit"
            } else if (loMoOption === "Market order") {
              loMoOptionData = "market"
            }
            params = {
              market: { pairName: klinesSymbol },
              type: actionType,
              orderType: conditional.toLowerCase(),
              baseAmount: amount,
              price: price,
              triggerPrice: triggerPrice,
              tpType: loMoOptionData,
            }
          } else if (conditional.toLowerCase() === 'oco') {
            params = {
              market: { pairName: klinesSymbol },
              type: actionType,
              orderType: conditional.toLowerCase(),
              baseAmount: amount,
              tpTriggerPrice: triggerPrice,
              tpOrderType: tpLimitOption,
              tpPrice: price,
              slTriggerPrice: slTriggerPrice,
              slOrderType: slLimitOption,
              slPrice: slLimitTriggerPrice,
            }
          }
        }

        const response = await postOrderAPI(params, totp)
        setLoading(false)

        if (response?.isError) {
          toast.error(response?.message)
        }
        if (response?.isSuccess) {
          toast.success('Order Added Successfully')
          dispatch(refetchOrders())
        }
        dispatch(fetchProfile({}))
        setShowTwoFactorAuthModal(false)
      } else {
        navigate('/login')
      }
    } catch (error) {
      setLoading(false)
      toast.error(error?.message)
      console.log(error?.message)
    }
  }


  return (
    <>
      <div className={styles.head}>
        <div className={styles.title}>{t(title)}</div>
        <div className={cn('counter', styles.counter)}>
          <Icon name="wallet" size="16" /> {content}
        </div>
      </div>

      {/* Render appropriate form fields based on order type */}
      {type === 'limit' && (
        <>
         <label className={styles.field}>
            <div className={styles.label}>{t('Price')}</div>
            <FormattedInput
              className={styles.input}
              type="text"
              name="price"
              value={price}
              onValueChange={setPrice}
              precision={pricePrecision}
            />
            <div className={styles.currency}>{priceSymbol}</div>
          </label>

        <MaxField label={"Amount"} value={amount} setValue={setAmount} valuePrecision={amountPrecision} valueSymbol={amountSymbol} onMaxClick={() => handleMaxClick()} />
      </>
      )}

      {type === 'market' && (
      <MaxField label={"Amount"} value={amount} setValue={setAmount} valuePrecision={amountPrecision} valueSymbol={amountSymbol} onMaxClick={() => handleMaxClick()} />

      )}

      {type === 'postonly' && (
        <>
          <label className={styles.field}>
            <div className={styles.label}>{t('Price')}</div>
            <FormattedInput
              className={styles.input}
              type="text"
              name="price"
              value={price}
              onValueChange={setPrice}
              precision={pricePrecision}
            />
            <div className={styles.currency}>{priceSymbol}</div>
          </label>

          <MaxField label={"Amount"} value={amount} setValue={setAmount} valuePrecision={amountPrecision} valueSymbol={amountSymbol} onMaxClick={() => handleMaxClick()} /> 


          <label className={styles.field}>
            <div className={styles.label}>{t('Total')}</div>
            <FormattedInput
              className={styles.input}
              type="text"
              name="totalPrice"
              value={totalPrice}
              onValueChange={(value) => {
                setTotalPrice(formatToPrecision(value, pricePrecision))
                setAmount(formatToPrecision(value / price, amountPrecision))
              }}
              precision={pricePrecision}
            />
            <div className={styles.currency}>{priceSymbol}</div>
          </label>
        </>
      )}

      {type === 'tpsl' && (
        <div className={styles.tpslContainer}>
          <>
            <TPSLPrice
              label="Trigger"
              setTriggerPrice={setTriggerPrice}
              triggerPrice={triggerPrice}
              priceSymbol={priceSymbol}
            />

            <label className={styles.field}>
              <div className={styles.label}>{t('Price')}</div>
              <FormattedInput
                className={styles.input}
                type="text"
                name="price"
                value={price}
                onValueChange={setPrice}
                precision={pricePrecision}
              />
              <div className={styles.currency}>{priceSymbol}</div>
            </label>

            <MaxField label={"Amount"} value={amount} setAmount={setAmount} amountPrecision={amountPrecision} amountSymbol={amountSymbol} onMaxClick={() => handleMaxClick()} />


            <label className={styles.field}>
              <div className={styles.label}>{t('Total')}</div>
              <FormattedInput
                className={styles.input}
                type="text"
                name="totalPrice"
                value={totalPrice}
                onValueChange={(value) => {
                  setTotalPrice(formatToPrecision(value, pricePrecision))
                  setAmount(formatToPrecision(value / price, amountPrecision))
                }}
                precision={pricePrecision}
              />
              <div className={styles.currency}>{priceSymbol}</div>
            </label>
          </>
        </div>
      )}
      {type === 'triggerorder' && (
        <div className={styles.tpslContainer}>
          <>
            <TriggerOrder
              label="Trigger"
              setOrderType={setOrderType}
              orderType={orderType}
              triggerOrderType={triggerOrderType}
              setTriggerOrderType={setTriggerOrderType}
              setTriggerPrice={setTriggerPrice}
              triggerPrice={triggerPrice}
              priceSymbol={priceSymbol}
              price={price}
              setPrice={setPrice}
            />
            {/*             
            <label className={styles.field}>
              <div className={styles.label}>{t('Price')}</div>
              <input
                className={styles.input}
                type="text"
                name="price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
              <div className={styles.currency}>{priceSymbol}</div>
            </label> */}

            <MaxField label={"Amount"} value={amount} setValue={setAmount} valuePrecision={amountPrecision} valueSymbol={amountSymbol} onMaxClick={() => handleMaxClick()} />


            <label className={styles.field}>
              <div className={styles.label}>{t('Total')}</div>
              <FormattedInput
                className={styles.input}
                type="text"
                name="totalPrice"
                value={totalPrice}
                onValueChange={(value) => {
                  setTotalPrice(formatToPrecision(value, pricePrecision))
                  setAmount(formatToPrecision(value / price, amountPrecision))
                }}
                precision={pricePrecision}
              />
              <div className={styles.currency}>{priceSymbol}</div>
            </label>
          </>
        </div>
      )}

      {stop && (
        <label className={styles.field}>
          <div className={styles.label}>{t('Stop')}</div>
          <div className={styles.currency}>{amountSymbol}</div>
        </label>
      )}

      {isTakeProfit && (
        <>
          <TPSLPrice label="TP trigger price" />
          <div className={styles.orderPrice}>
            <TPSLPrice label="TP order price" isMarket marketLabel="Market" />
          </div>
        </>
      )}

      {isStopLoss && (
        <>
          <TPSLPrice label="SL trigger price" />
          <div className={styles.orderPrice}>
            <TPSLPrice label="SL order price" isMarket marketLabel="Market" />
          </div>
        </>
      )}

      <label className={`${styles.field} ${styles.fieldPadding}`}>
        <div className={styles.label}>Fees Amount : {feesAmount || 0} {priceSymbol}</div>
      </label>
      <TwoFactorAuth
        isOpen={showTwoFactorAuthModal}
        onClose={() => setShowTwoFactorAuthModal(false)}
        onSubmit={(totp) => { handleMaxClick(totp) }}
        currentModal={"verifyTwoFactorAuthModal"+buttonText}
      />
      <EnableTwoFactorAuthModal
        isOpen={showEnableTwoFactorAuthModal}
        onClose={() => setShowEnableTwoFactorAuthModal(false)}
        onSubmit={(totp) => { handleMaxClick(totp) }}
        currentModal={'enableTwoFactorAuthModal'+buttonText}
      />
      <button
        type='button'
        disabled={loading}
        className={cn(classButton, styles.button)}
        onClick={() => handleClickWithData()}
      >
        {loading ? <Loader /> : t(buttonText)}
      </button>
    </>
  )
}

// Track price updates per symbol using a Map
const priceUpdateTracker = new Map()

// Custom comparison: re-render on any non-price prop change, and allow up to 10 consecutive currentPrice updates
const areEqual = (prevProps, nextProps) => {
  const prevKeys = Object.keys(prevProps)
  const nextKeys = Object.keys(nextProps)

  // If props shape changed, reset window and re-render
  if (prevKeys.length !== nextKeys.length) {
    priceUpdateTracker.set(nextProps.klinesSymbol, 0)
    return false
  }

  // Check all props except currentPrice
  for (const key of prevKeys) {
    if (key === 'currentPrice') continue
    if (prevProps[key] !== nextProps[key]) {
      // Any non-price prop changed -> reset counter and re-render
      priceUpdateTracker.set(nextProps.klinesSymbol, 0)
      return false
    }
  }

  // Only currentPrice changed
  if (prevProps.currentPrice !== nextProps.currentPrice) {
    const count = priceUpdateTracker.get(nextProps.klinesSymbol) || 0
    if (count < 10) {
      priceUpdateTracker.set(nextProps.klinesSymbol, count + 1)
      return false // allow up to 10 price updates
    }
    return true // skip re-render beyond 10 price changes
  }

  // No changes -> skip
  return true
}

export default memo(Action, areEqual)